import React from "react";
import { Typography } from "@mui/material";
import CustomIcon from "../../../../Components/CustomIcon";
import AlertConfigurationMap from "./columnTypes/AlertConfigurationMap";
import ProgressBarColumnView from "./columnTypes/ProgressBarColumnView";
import ActionMenu from "./columnTypes/ActionMenu";
import Timestamp from "./columnTypes/Timestamp";
import TableTagView from "./columnTypes/TableTagView";
import TableIconView from "./columnTypes/TableIconView";
import SecondsFormatter, { SecondsFormatterDetailed } from "../../../../Helper/Formatters/SecondsFormatter";
import { getFormattedResolutionTime } from "../../../../Helper/DatePicker/DateFormatters";
import TextWithCopy from "./columnTypes/TextWithCopy";
import { isNumber } from "../../../../Helper/Common/common_helpers";
import DeviceIDLink from "./columnTypes/DeviceIDLink";
import PingStatusTooltip from "../../../../Components/PingStatus/PingStatusTooltip";
import { toTitleCase } from "../../../../Helper/Formatters/TextFormatter";
import { formatKmAxisTick } from "../../../../Helper/Formatters/NumberFormatter";
import FieldStatusIndicator, { getIndicatorNames } from "../../../../Components/FieldStatusIndicator";


/**
 * Converts text to snake_case format
 * @param {string} text - Text to convert
 * @returns {string} Snake case formatted text
 */
const toSnakeCase = (text) => {
    if (!text || typeof text !== 'string') {
        return text;
    }
    return text
        .replace(/([a-z])([A-Z])/g, '$1_$2') // Convert camelCase to snake_case
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .toLowerCase();
};

/**
 * Applies text formatting based on the format type
 * @param {string} text - Text to format
 * @param {string} format - Format type ('title', 'snake', etc.)
 * @returns {string} Formatted text
 */
const applyTextFormat = (text, format) => {
    if (!text || !format) {
        return text;
    }

    switch (format.toLowerCase()) {
        case 'title':
            return toTitleCase(text);
        case 'snake':
            return toSnakeCase(text);
        default:
            return text;
    }
};

const renderColumnValue = ({
    record,
    config,
    customRenderers = {}
}) => {

    const value = record?.[config?.dataIndex];
    const decimalPlaces = config?.decimalPlaces
    const type = config?.valueRenderingType || 'text'
    const copyToClipBoardTypeName = config?.copyToClipBoardTypeName
    const textFormat = config?.textFormat
    let defaultValue = config.defaultValue ? config.defaultValue : value


    if (customRenderers[type]) {
        return customRenderers[type](record, config);
    }

    switch (type) {
        case 'ping-status-icon':
            return (
                <PingStatusTooltip online={value === "Active"}>
                    <CustomIcon name={value === "Active" ? "online" : "offline"} style={{ width: "24px" }} />
                </PingStatusTooltip>
            );
        case 'text':
            const displayValue = value || defaultValue;
            const formattedValue = textFormat ? applyTextFormat(displayValue, textFormat) : displayValue;
            return <Typography variant="fw400s14lh24c3D4B5A">{formattedValue}</Typography>;

        case 'text-with-copy':
            // Nothing to copy means no copy button: it would offer to put an empty
            // string (or a placeholder dash) on the clipboard.
            if (value === null || value === undefined || value === '') {
                return <Typography variant="fw400s14lh24c3D4B5A">{defaultValue}</Typography>;
            }
            return <TextWithCopy text={value} type={copyToClipBoardTypeName} />;

        case 'number':
            return <Typography sx={{
                textAlign: 'right',
                display: 'flex',
                justifyContent: 'flex-end'
            }} variant="fw400s14lh24c3D4B5A">{typeof value === 'number' ? value?.toFixed(decimalPlaces) : defaultValue}</Typography>;
        case 'distance-km': {
            // Scales km values >= 1000 to "Nk km" (e.g. 78967 -> "79k km"); below 1000 -> "N km".
            // parseFloat guard: API can return numbers as strings.
            const kmValue = parseFloat(value);
            return <Typography sx={{
                textAlign: 'right',
                display: 'flex',
                justifyContent: 'flex-end'
            }} variant="fw400s14lh24c3D4B5A">{isNaN(kmValue) ? defaultValue : formatKmAxisTick(kmValue)}</Typography>;
        }
        case 'timestamp':
            return <Timestamp config={config} record={record} />

        case 'digital-twin-list-series-parallel':
            const level = config?.seriesParallelConfigLevel
            const tableValue = level === "pack"
                ? `${record?.seriesPacks}S / ${record?.parallelPacks}P`
                : level === "module"
                    ? `${record?.seriesModulesInPack}S / ${record?.parallelModulesInPack}P`
                    : `${record?.seriesModulesInPack * record?.seriesCellsInModule}S / ${record?.parallelModulesInPack * record?.parallelCellsInModule}P`

            return <Typography variant="tableRow">{tableValue}</Typography>;
        case 'digital-twin-list-past-current':
            return <Typography variant="fw400s14lh24c3D4B5A">{record?.minCurrentPack !== null && record?.maxCurrentPack !== null ? `${record?.minCurrentPack} min / ${record?.maxCurrentPack} max` : ""}</Typography>;
        case 'digital-twin-list-past-voltage':
            return <Typography variant="fw400s14lh24c3D4B5A">{record?.minVoltageCell !== null && record?.maxVoltageCell !== null ? `${record?.minVoltageCell} min / ${record?.maxVoltageCell} max` : ""}</Typography>;
        case 'digital-twin-list-past-temperature':
            return <Typography variant="fw400s14lh24c3D4B5A">{record?.minTemperaturePack !== null && record?.maxTemperaturePack !== null ? `${record?.minTemperaturePack} min / ${record?.maxTemperaturePack} max` : ""}</Typography>;
        case 'alert-configuration-map':
            return <AlertConfigurationMap config={record} />

        case 'device-id-link':
            return <DeviceIDLink record={record} config={config} />

        case 'action-menu':
            let actions = config.actions ? config.actions : []
            return <ActionMenu record={record} config={config} actions={actions} >Menu</ActionMenu>

        case 'age':
            let age = value
            let ageUnit = config?.ageUnit ? config?.ageUnit : 'days'
            let requiredUnit = config?.requiredAgeUnit ? config?.requiredAgeUnit : 'months'
            if (!age) {
                return <Typography variant="fw400s14lh24c3D4B5A">Not Available</Typography>
            }
            age = (age / 30).toFixed(0)

            return <Typography sx={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }} variant="fw400s14lh24c3D4B5A">{age}</Typography>;

        case 'progress-bar':
            const barColor = '#000'
            const startProgressFrom = config?.startProgressFrom ? config?.startProgressFrom : 0
            const unitPostFix = config?.unitPostfix ? config?.unitPostfix : ""
            return <ProgressBarColumnView
                minProgress={startProgressFrom}
                defaultText={"Not Available"}
                value={value ? value?.toFixed(decimalPlaces) : 0}
                barColor={barColor}
                record={record}
                config={config}
                unitPostFix={unitPostFix}

            />

        case 'tag':
            return <TableTagView record={record} config={config} value={value} />
        case 'icon':
            return <TableIconView record={record} config={config} />

        case 'deltaTime 2':
            const starting = config?.starting ? config?.starting : 'seconds'
            let deltaTime = value

            if (!deltaTime) {
                return <Typography variant="fw400s14lh24c3D4B5A">{config?.defaultValue || 'Not Available'}</Typography>
            }

            if (starting === 'minutes') {
                deltaTime = deltaTime * 60
            }

            if (starting === 'hours') {
                deltaTime = deltaTime * 60 * 60
            }

            if (starting === 'days') {
                deltaTime = deltaTime * 60 * 60 * 24
            }

            return <Typography variant="fw400s14lh24c3D4B5A">{SecondsFormatterDetailed(deltaTime)}</Typography>;

        // will fix it when we will deploy
        case 'duration':
        case 'deltaTime':

            if (!isNumber(value)) {
                return <Typography variant="fw400s14lh24c3D4B5A">
                    {config?.defaultValue || 'Not Available'}
                </Typography>
            }

            return <Typography variant="fw400s14lh24c3D4B5A">{getFormattedResolutionTime(value)}</Typography>;

        default:
            return null;



    }
}

/**
 * Column types that render DeviceIDLink, which anchors its own field status marker
 * around the text. Wrapping them again here would double the marker and park the
 * second one at the far edge of the cell.
 */
const SELF_ANCHORED_TYPES = ['device-id-link', 'battery-pack-plate-link'];

/**
 * Renders a cell value, and hangs a field status marker off its corner when the
 * column config names one and the org has the feature enabled.
 *
 * This is the single injection point for markers on table cells, so any column type -
 * text, timestamp, tag, progress bar - can carry one from config alone.
 */
export const TableColumnValueRenderer = (props) => {
    const node = renderColumnValue(props);

    const type = props?.config?.valueRenderingType || 'text';
    const indicatorName = getIndicatorNames(props?.config?.indicators)[0];
    if (!indicatorName || SELF_ANCHORED_TYPES.includes(type)) return node;

    return (
        <FieldStatusIndicator name={indicatorName} record={props?.record}>
            {node}
        </FieldStatusIndicator>
    );
};
